import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Tv, ShieldCheck, Search } from "lucide-react";

/**
 * Compact badge showing which data source(s) the scoring engine used
 * for this stock's fundamentals. Surfaces the new
 * `composite_scores.fundamentals_data_source` field added by the Phase 2
 * "annual-report-first" scoring refactor (2026-05).
 *
 *   "annual_report"          → audited annual report (Claude OCR)
 *   "annual_report+csepal"   → annual report + CSEPal corporate-action-adjusted EPS
 *   "tradingview"            → TradingView fallback (no analysed annual report yet)
 *   "manual"                 → admin-typed override (data_quality_service)
 *   "none" / undefined       → no fundamentals available
 */
export default function FundamentalsSourceBadge({
  source,
  annualYear,
  corporateActionAdjusted,
  testId = "fundamentals-source-badge",
}) {
  if (!source || source === "none") return null;

  const config = {
    annual_report: {
      label: annualYear ? `AR ${annualYear}` : "AR",
      icon: FileText,
      cls: "border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      tip: (
        <>
          <p className="font-medium">Annual Report</p>
          <p className="text-xs text-muted-foreground mt-1">
            Fundamentals (EPS, BVPS, ROE, DPS) sourced from the audited
            annual report
            {annualYear ? ` for FY ${annualYear}` : ""}. P/E, P/B and
            dividend yield are recomputed from the report values + latest
            close, so they reflect the most recent corporate-action
            adjustments.
          </p>
        </>
      ),
    },
    "annual_report+csepal": {
      label: annualYear ? `AR ${annualYear} + CSEPal` : "AR + CSEPal",
      icon: Search,
      cls: "border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      tip: (
        <>
          <p className="font-medium">Annual Report + CSEPal</p>
          <p className="text-xs text-muted-foreground mt-1">
            Annual report fundamentals adjusted with CSEPal corporate-action-adjusted
            EPS (used when annual report EPS differs by more than 30%, e.g., post
            rights-issue).
          </p>
        </>
      ),
    },
    csepal: {
      label: "CSEPal",
      icon: Search,
      cls: "border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      tip: (
        <>
          <p className="font-medium">CSEPal</p>
          <p className="text-xs text-muted-foreground mt-1">
            Fundamentals sourced from CSEPal (corporate-action-adjusted).
          </p>
        </>
      ),
    },
    tradingview: {
      label: "TV",
      icon: Tv,
      cls: "border-amber-600/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      tip: (
        <>
          <p className="font-medium">TradingView (fallback)</p>
          <p className="text-xs text-muted-foreground mt-1">
            No audited annual report has been analysed for this stock yet,
            so fundamentals fall back to TradingView. TV values can be
            stale around corporate actions (rights issues, splits) — upload
            the latest annual report for higher-accuracy scoring.
          </p>
        </>
      ),
    },
    manual: {
      label: "Manual",
      icon: ShieldCheck,
      cls: "border-sky-600/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
      tip: (
        <>
          <p className="font-medium">Manual override</p>
          <p className="text-xs text-muted-foreground mt-1">
            Fundamentals were hand-corrected by an administrator and are
            locked from automated overwrites.
          </p>
        </>
      ),
    },
  };

  const cfg = config[source] || config.tradingview;
  const Icon = cfg.icon;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium leading-tight ${cfg.cls}`}
          >
            <Icon className="h-3 w-3" />
            <span>{cfg.label}</span>
            {corporateActionAdjusted && (
              <span
                title="Corporate action adjusted"
                className="ml-0.5 inline-block rounded-full bg-emerald-600 px-1 text-[9px] text-white"
                data-testid={`${testId}-corp-action`}
              >
                CA
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          {cfg.tip}
          {corporateActionAdjusted && (
            <p className="text-xs mt-2 text-muted-foreground">
              Flagged as <strong>corporate-action adjusted</strong>{" "}
              (rights issue / split / large share-count change detected).
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
